import { useState, useEffect } from 'react';
import { DEFAULT_ANNOTATION_STYLE, DEFAULT_TEXTBOX_STYLE } from '@/utils/cad-sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Trash2 } from 'lucide-react';

const COLOR_SWATCHES = ['#000000', '#ef4444', '#3b82f6', '#22c55e', '#f59e0b'];

function PropertyInput({ value, onChange, className }) {
  const [localVal, setLocalVal] = useState(value || '');
  
  useEffect(() => {
    setLocalVal(value || '');
  }, [value]);
  
  const handleBlur = () => {
    if (localVal !== value) {
      onChange(localVal);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };
  
  return (
    <Input
      value={localVal}
      onChange={e => setLocalVal(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
    />
  );
}

export function AnnotationPropertiesPanel({
  selectedAnnotation,
  selectedTextBox,
  selectedHighlight,
  activeTool,
  activeToolStyle,
  onUpdateActiveToolStyle,
  onUpdateAnnotationStyle,
  onUpdateAnnotationValue,
  onDeleteAnnotation,
  onUpdateTextBoxStyle,
  onUpdateTextBoxValue,
  onDeleteTextBox,
  onUpdateHighlight,
  onDeleteHighlight,
}) {
  if (selectedAnnotation) {
    const style = selectedAnnotation.style || DEFAULT_ANNOTATION_STYLE;
    return (
      <div className="p-3 space-y-3">
        <div className="text-xs font-semibold text-foreground">Dimension Properties</div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Value</Label>
          <PropertyInput value={selectedAnnotation.value} onChange={val => onUpdateAnnotationValue?.(selectedAnnotation.id, val)} className="h-7 text-xs" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Color</Label>
          <div className="flex gap-1">
            {COLOR_SWATCHES.map(c => (
              <button key={c} className={`w-5 h-5 rounded-full border-2 ${style.lineColor === c ? 'border-foreground' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => onUpdateAnnotationStyle?.(selectedAnnotation.id, { lineColor: c, textColor: c })} />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Line Thickness</Label>
          <Slider value={[style.lineWidth]} onValueChange={([v]) => onUpdateAnnotationStyle?.(selectedAnnotation.id, { lineWidth: v })} min={0.2} max={2} step={0.1} className="w-full" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Font Size</Label>
          <Slider value={[style.fontSize]} onValueChange={([v]) => onUpdateAnnotationStyle?.(selectedAnnotation.id, { fontSize: v })} min={1.5} max={6} step={0.2} className="w-full" />
        </div>
        <Button variant="destructive" size="sm" className="w-full h-7 text-xs" onClick={() => onDeleteAnnotation?.(selectedAnnotation.id)}>
          <Trash2 className="h-3 w-3 mr-1" /> Delete Dimension
        </Button>
      </div>
    );
  }

  if (selectedTextBox) {
    const style = selectedTextBox.style || DEFAULT_TEXTBOX_STYLE;
    return (
      <div className="p-3 space-y-3">
        <div className="text-xs font-semibold text-foreground">Text Properties</div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Text</Label>
          <PropertyInput value={selectedTextBox.value} onChange={val => onUpdateTextBoxValue?.(selectedTextBox.id, val)} className="h-7 text-xs" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Color</Label>
          <div className="flex gap-1">
            {COLOR_SWATCHES.map(c => (
              <button key={c} className={`w-5 h-5 rounded-full border-2 ${style.color === c ? 'border-foreground' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => onUpdateTextBoxStyle?.(selectedTextBox.id, { color: c })} />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Font Size</Label>
          <Slider value={[style.fontSize]} onValueChange={([v]) => onUpdateTextBoxStyle?.(selectedTextBox.id, { fontSize: v })} min={1.5} max={6} step={0.2} className="w-full" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Bold</Label>
          <Button variant={style.bold ? 'default' : 'outline'} size="sm" className="h-7 text-xs font-bold" onClick={() => onUpdateTextBoxStyle?.(selectedTextBox.id, { bold: !style.bold })}>B</Button>
        </div>
        <Button variant="destructive" size="sm" className="w-full h-7 text-xs" onClick={() => onDeleteTextBox?.(selectedTextBox.id)}>
          <Trash2 className="h-3 w-3 mr-1" /> Delete Text
        </Button>
      </div>
    );
  }

  if (selectedHighlight) {
    return (
      <div className="p-3 space-y-3">
        <div className="text-xs font-semibold text-foreground">Highlight Properties</div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Color</Label>
          <div className="flex gap-1">
            {['#facc15', '#ef4444', '#3b82f6', '#22c55e', '#f97316'].map(c => (
              <button key={c} className={`w-5 h-5 rounded-full border-2 ${selectedHighlight.color === c ? 'border-foreground' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => onUpdateHighlight?.(selectedHighlight.id, { color: c })} />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Opacity</Label>
          <Slider value={[selectedHighlight.opacity]} onValueChange={([v]) => onUpdateHighlight?.(selectedHighlight.id, { opacity: v })} min={0.1} max={1} step={0.05} className="w-full" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px]">Brush Size</Label>
          <Slider value={[selectedHighlight.strokeWidth]} onValueChange={([v]) => onUpdateHighlight?.(selectedHighlight.id, { strokeWidth: v })} min={1} max={10} step={0.5} className="w-full" />
        </div>
        <Button variant="destructive" size="sm" className="w-full h-7 text-xs" onClick={() => onDeleteHighlight?.(selectedHighlight.id)}>
          <Trash2 className="h-3 w-3 mr-1" /> Delete Highlight
        </Button>
      </div>
    );
  }

  const toolLabel = activeTool === 'ruler' ? 'Dimension' : activeTool === 'text' ? 'Text' : activeTool === 'highlight' ? 'Highlight' : 'Tool';

  return (
    <div className="p-3 space-y-3">
      <div className="text-xs font-semibold text-foreground">{toolLabel} Defaults</div>
      <div className="space-y-1.5">
        <Label className="text-[10px]">Color</Label>
        <div className="flex gap-1">
          {COLOR_SWATCHES.map(c => (
            <button key={c} className={`w-5 h-5 rounded-full border-2 ${activeToolStyle.strokeColor === c ? 'border-foreground' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => onUpdateActiveToolStyle({ ...activeToolStyle, strokeColor: c })} />
          ))}
        </div>
      </div>
      {(activeTool === 'ruler' || activeTool === 'select') && (
        <div className="space-y-1.5">
          <Label className="text-[10px]">Line Thickness</Label>
          <Slider value={[activeToolStyle.lineThickness]} onValueChange={([v]) => onUpdateActiveToolStyle({ ...activeToolStyle, lineThickness: v })} min={0.2} max={2} step={0.1} className="w-full" />
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-[10px]">Font Size</Label>
        <Slider value={[activeToolStyle.fontSize]} onValueChange={([v]) => onUpdateActiveToolStyle({ ...activeToolStyle, fontSize: v })} min={1.5} max={6} step={0.2} className="w-full" />
      </div>
      {activeTool === 'highlight' && (
        <div className="space-y-1.5">
          <Label className="text-[10px]">Opacity</Label>
          <Slider value={[activeToolStyle.opacity]} onValueChange={([v]) => onUpdateActiveToolStyle({ ...activeToolStyle, opacity: v })} min={0.1} max={1} step={0.05} className="w-full" />
        </div>
      )}
      {activeTool === 'text' && (
        <div className="space-y-1.5">
          <Label className="text-[10px]">Bold</Label>
          <Button variant={activeToolStyle.bold ? 'default' : 'outline'} size="sm" className="h-7 text-xs font-bold" onClick={() => onUpdateActiveToolStyle({ ...activeToolStyle, bold: !activeToolStyle.bold })}>B</Button>
        </div>
      )}
      <div className="text-[10px] text-muted-foreground text-center pt-2">
        {activeTool === 'select' ? 'Select an annotation to edit' : 'Settings apply to next created item'}
      </div>
    </div>
  );
}
